import { QueryClient } from 'react-query';
import { waitUntilIdle } from 'utils/api';
import { reloadWithFreshDocument } from 'utils/staleBundle';

// The magic-link guest CV builder holds its session only in memory. A forced full-page
// reload there drops that session mid-edit and (when SW updates arrive in bursts) drives a
// redeem/reload storm — so we never auto-reload while an applicant is on that page. The
// guest flow is a self-contained SPA page and picks up new assets on the next navigation.
const GUEST_CV_PATH = '/guest/curriculumVitaes';

const isActiveGuestCvSession = () =>
  window.location.pathname.startsWith(GUEST_CV_PATH);

export const handleControllerChange = (queryClient: QueryClient) => {
  // Delay reload to allow the pending network requests to complete
  setTimeout(() => {
    void (async () => {
      // Making sure all queries are idle before reloading
      await waitUntilIdle(queryClient);

      console.warn(
        'New Service Worker has taken control. Reloading page to apply update and prevent mismatch errors.'
      );

      // A plain reload can be answered from the HTTP cache with the PREVIOUS index.html,
      // whose entry script the deploy has just deleted: the page then stays blank. Refetch
      // the document past the cache first so the reload gets the new build.
      await reloadWithFreshDocument();
    })();
  }, 500);
};

export const listenToSWControllerChange = (queryClient: QueryClient) => {
  if ('serviceWorker' in navigator) {
    // On a first-ever visit there is no controller yet; the first service worker fires
    // `controllerchange` purely to TAKE control (skipWaiting + clients.claim). Reloading
    // then is pointless — there's no previous version to mismatch — and disruptive: on the
    // guest CV entry page it races the magic-link redeem-on-mount, leaving the applicant on
    // a stuck/disabled screen until they re-open the link.
    //
    // Skip ONLY that first-install claim. The flag is mutable and flips after it, so any
    // later `controllerchange` — a genuine SW update, even within the same long-lived tab —
    // still reloads to apply the new assets. A returning visitor starts with a controller,
    // so updates reload immediately.
    let hadController = Boolean(navigator.serviceWorker.controller);
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!hadController) {
        hadController = true;
        return;
      }
      // Never yank the rug out from under an in-progress guest CV session.
      if (isActiveGuestCvSession()) {
        return;
      }
      handleControllerChange(queryClient);
    });
  }
};
